import { useId } from 'react';
import {
  configApiRef,
  useApi,
  type ConfigApi,
} from '@backstage/core-plugin-api';
import { Button, DialogTrigger, Link, Popover, Text } from '@backstage/ui';
import { waadooIcons } from '@internal/plugin-waadoo-core';

const PasswordReset = waadooIcons.passwordReset;
const Support = waadooIcons.support;
const Group = waadooIcons.directoryGroup;
const Expand = waadooIcons.expand;

/** Only these schemes make an `href` here: never `javascript:`. */
const SAFE_HREF = /^(https?:|mailto:)/i;

interface SupportContact {
  href: string;
  /** What the entry shows under « Contacter le support ». */
  label: string;
}

/**
 * The support contact of Backstage's standard `app.support` (schema of
 * @backstage/core-app-api): the first link of its `items`, else its `url`.
 * Read here rather than through core-components' `useSupportConfig`, which
 * falls back to the Backstage project's own GitHub links when unset — the
 * entry is hidden then.
 */
function readSupport(config: ConfigApi): SupportContact | undefined {
  const support = config.getOptionalConfig('app.support');
  if (!support) {
    return undefined;
  }
  const link = (support.getOptionalConfigArray('items') ?? []).flatMap(
    item => item.getOptionalConfigArray('links') ?? [],
  )[0];
  const href =
    link?.getOptionalString('url') ?? support.getOptionalString('url');
  if (!href || !SAFE_HREF.test(href)) {
    return undefined;
  }
  return {
    href,
    label: link?.getOptionalString('title') ?? href.replace(/^mailto:/i, ''),
  };
}

function readPasswordResetUrl(config: ConfigApi): string | undefined {
  const url = config.getOptionalString('app.signIn.passwordResetUrl');
  return url && SAFE_HREF.test(url) && !/^mailto:/i.test(url) ? url : undefined;
}

function NewTab() {
  return <span className="waadoo-visually-hidden">(nouvel onglet)</span>;
}

/**
 * « Besoin d’aide ? » under the sign-in card: a button that opens a panel
 * right below it — BUI's Popover in a DialogTrigger, i.e. react-aria's: a
 * `dialog` that takes the focus, closes on Escape or a click outside, and
 * gives the focus back to the button. Each entry shows only when its address
 * is configured.
 */
export function SignInHelp() {
  const config = useApi(configApiRef);
  const titleId = useId();
  const passwordResetUrl = readPasswordResetUrl(config);
  const support = readSupport(config);
  const supportInNewTab = support && !/^mailto:/i.test(support.href);

  return (
    <DialogTrigger>
      <Button
        variant="tertiary"
        size="small"
        className="waadoo-signin__help-trigger"
        iconEnd={
          <Expand
            className="waadoo-signin__help-caret"
            aria-hidden
            focusable="false"
          />
        }
      >
        Besoin d’aide ?
      </Button>
      <Popover
        placement="bottom"
        className="waadoo-signin-help"
        aria-labelledby={titleId}
      >
        <Text as="h2" id={titleId} variant="body-medium" weight="bold">
          Besoin d’aide ?
        </Text>
        <ul className="waadoo-signin-help__list">
          {passwordResetUrl && (
            <li className="waadoo-signin-help__item">
              <PasswordReset
                className="waadoo-signin-help__icon"
                aria-hidden
                focusable="false"
              />
              <div className="waadoo-signin-help__text">
                <Link
                  href={passwordResetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body-medium"
                  weight="bold"
                >
                  Mot de passe oublié ? <NewTab />
                </Link>
                <Text as="p" variant="body-small" color="secondary">
                  Réinitialisez-le sur le portail de connexion Waadoo.
                </Text>
              </div>
            </li>
          )}
          {support && (
            <li className="waadoo-signin-help__item">
              <Support
                className="waadoo-signin-help__icon"
                aria-hidden
                focusable="false"
              />
              <div className="waadoo-signin-help__text">
                <Link
                  href={support.href}
                  variant="body-medium"
                  weight="bold"
                  {...(supportInNewTab && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                >
                  Contacter le support{supportInNewTab && ' '}
                  {supportInNewTab && <NewTab />}
                </Link>
                <Text as="p" variant="body-small" color="secondary">
                  {support.label}
                </Text>
              </div>
            </li>
          )}
          <li className="waadoo-signin-help__item waadoo-signin-help__note">
            <Group
              className="waadoo-signin-help__icon"
              aria-hidden
              focusable="false"
            />
            <Text as="p" variant="body-small" color="secondary">
              Connecté mais aucun projet visible ? Demandez à l’administrateur
              de votre projet de vous ajouter à son groupe.
            </Text>
          </li>
        </ul>
      </Popover>
    </DialogTrigger>
  );
}
